import { useEffect, useRef, useState } from 'react';

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** rAF-throttled scroll/resize subscription. */
function onScroll(handler: () => void) {
  let ticking = false;
  const run = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      handler();
    });
  };
  run();
  window.addEventListener('scroll', run, { passive: true });
  window.addEventListener('resize', run, { passive: true });
  return () => {
    window.removeEventListener('scroll', run);
    window.removeEventListener('resize', run);
  };
}

/** 0 → 1 progress through the whole document. */
export function usePageProgress(): number {
  const [progress, setProgress] = useState(0);
  useEffect(
    () =>
      onScroll(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
      }),
    []
  );
  return progress;
}

/**
 * 0 → 1 as an element travels through the viewport. Reaches 1 when the element's
 * bottom is `endOffset` viewport-heights above the bottom edge. Returns 1
 * immediately under reduced motion.
 */
export function useElementProgress<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  { startOffset = 0.15, endOffset = 0.35 } = {}
): number {
  const [progress, setProgress] = useState(reducedMotion() ? 1 : 0);
  const last = useRef(-1);

  useEffect(() => {
    if (reducedMotion()) return;
    return onScroll(() => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * (1 - startOffset); // element top crosses this line → 0
      const end = vh * endOffset; // element bottom reaches this line → 1
      const raw = (start - rect.top) / (rect.height + start - end);
      const p = Math.min(1, Math.max(0, raw));
      if (Math.abs(p - last.current) > 0.004) {
        last.current = p;
        setProgress(p);
      }
    });
  }, [ref, startOffset, endOffset]);

  return progress;
}
